import { Close } from '@mui/icons-material';
import { Box, IconButton, Modal, Switch, Typography } from '@mui/material';
import type React from 'react';
import { useNotificationSettings } from '../hooks/useNotificationSetting';
import { NotificationSettingsSkeleton } from './Skeletons/NotificationSettingsSkeleton';
import { iconMap } from '../constants';
import { trackEvent } from '../../../modules/analytics/trackingMiddleware';
import { NOTIFICATION_SETTING_CHANGED } from '../../../modules/analytics/events';

const modalStyles = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: 350, sm: 800 },
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    maxHeight: '80vh',
    overflow: 'auto',
};
interface NotificationSettingsModalProps {
    open: boolean;
    onClose: () => void;
}

export const NotificationSettingsModal: React.FC<NotificationSettingsModalProps> = ({
    open,
    onClose,
}) => {
    const {
        settings,
        isLoading: isLoadingSettings,
        error: settingsError,
        updateSettings,
    } = useNotificationSettings();
    const { moduleList, selectedModules } = (settings as any) || { moduleList: [], selectedModules: [] };
    const handleModuleSettingChange = (moduleName: string, enabled: boolean) => {
        const moduleList = !enabled
            ? [...selectedModules, moduleName]
            : selectedModules?.filter(name => name !== moduleName);

        updateSettings({ moduleList });
        trackEvent(NOTIFICATION_SETTING_CHANGED, { moduleName, enabled });
    };


    const isLoading = isLoadingSettings;
    const hasError = settingsError;

    return (
        <Modal open={open} onClose={onClose} aria-labelledby="settings-modal-title">
            <Box sx={modalStyles}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography id="settings-modal-title" variant="h6" component="h2">
                        Notification Settings
                    </Typography>
                    <IconButton onClick={onClose}>
                        <Close />
                    </IconButton>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                        Module & Description
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="medium">
                        Status
                    </Typography>
                </Box>

                {isLoading ? (
                    <NotificationSettingsSkeleton />
                ) : hasError ? (
                    <Box sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>
                        Failed to load notification settings
                    </Box>
                ) : (
                    <Box sx={{ maxHeight: '65vh', overflow: 'auto' }}>
                        {moduleList?.map(module => {
                            const IconComponent = iconMap[module.name];
                            return (
                                <Box
                                    key={module.id}
                                    sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {IconComponent && <IconComponent />}
                                                <Typography variant="body1" fontWeight="medium" sx={{ ml: 1 }}>
                                                    {module.name}
                                                </Typography>
                                            </Box>
                                            {module.description && <Typography marginTop={1} variant='h4'>{module.description}</Typography>}
                                        </Box>
                                        <Switch
                                            checked={!selectedModules?.includes(module.name) || false}
                                            onChange={e => handleModuleSettingChange(module.name, e.target.checked)}
                                            sx={{
                                                ml: 2,
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: (theme) => theme.palette.Primary.main,
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: (theme) => theme.palette.Primary.main,
                                                },
                                            }}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default NotificationSettingsModal;
